export type BlogFaqItem = { question: string; answer: string };

const FAQ_SECTION_HEADING =
  /^(questions\s+fr[eé]quentes|frequently\s+asked\s+questions|preguntas\s+frecuentes|h[aä]ufige\s+fragen|domande\s+frequenti)$/i;

function stripMarkdownToPlain(text: string): string {
  return String(text ?? '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]+`/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]+\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^>\s?/gm, '')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/[*_~]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

type Heading = { lineIndex: number; level: number; text: string };

/**
 * #1153 — corps de réponse FAQ : s'arrête au premier séparateur horizontal (---) et ignore
 * les lignes de tableau markdown (| a | b |, |---|), qui n'ont pas leur place dans le JSON-LD.
 */
function faqAnswerBody(bodyLines: string[]): string {
  const out: string[] = [];
  for (const line of bodyLines) {
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) break;
    if (/^\s*\|.*\|\s*$/.test(line)) continue;
    out.push(line);
  }
  return out.join('\n');
}

function parseHeadings(content: string): { lines: string[]; headings: Heading[] } {
  const lines = String(content ?? '').split(/\r?\n/);
  const headings: Heading[] = [];
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(/^(#{1,6})\s+(.+)$/);
    if (m) {
      headings.push({ lineIndex: i, level: m[1].length, text: m[2].trim() });
    }
  }
  return { lines, headings };
}

function faqSectionBounds(headings: Heading[]): { startLine: number; endLine: number } | null {
  const start = headings.find(
    (h) => h.level === 2 && FAQ_SECTION_HEADING.test(h.text.replace(/\*+/g, '').trim())
  );
  if (!start) return null;
  const end = headings.find((h) => h.level === 2 && h.lineIndex > start.lineIndex);
  return {
    startLine: start.lineIndex,
    endLine: end ? end.lineIndex : Number.POSITIVE_INFINITY,
  };
}

function isFaqQuestionHeading(
  h: Heading,
  bounds: { startLine: number; endLine: number } | null
): boolean {
  if (h.level === 1) return false;
  if (bounds && h.lineIndex === bounds.startLine) return false;

  const inSection =
    bounds !== null && h.lineIndex > bounds.startLine && h.lineIndex < bounds.endLine;

  if (h.text.endsWith('?')) return true;
  if (inSection && h.level >= 3) return true;
  return false;
}

export function extractBlogFaqFromMarkdown(content: string): BlogFaqItem[] {
  const { lines, headings } = parseHeadings(content);
  if (!headings.length) return [];

  const bounds = faqSectionBounds(headings);
  const items: BlogFaqItem[] = [];

  for (let i = 0; i < headings.length; i++) {
    const h = headings[i];
    if (!isFaqQuestionHeading(h, bounds)) continue;

    const nextLine = headings[i + 1]?.lineIndex ?? lines.length;
    const body = faqAnswerBody(lines.slice(h.lineIndex + 1, nextLine));
    const answer = stripMarkdownToPlain(body);
    if (!answer) continue;

    items.push({ question: h.text, answer });
  }

  return items;
}

export function resolveBlogArticleFaqItems(article: {
  content?: string;
  faq_items?: BlogFaqItem[];
}): BlogFaqItem[] {
  const fromMarkdown = extractBlogFaqFromMarkdown(article?.content || '');
  if (fromMarkdown.length >= 2) return fromMarkdown;

  const stored = Array.isArray(article?.faq_items) ? article.faq_items : [];
  return stored.filter(
    (item) =>
      item?.question?.trim() &&
      item?.answer?.trim() &&
      String(item.question).trim().endsWith('?')
  );
}
